import { MutableRefObject, PropsWithChildren, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { debugLog } from '../common/debug';
import {
  ComponentType,
  LuiModalAsyncContext,
  openModalOnAllWindows,
  PromiseWithResolve,
  ShowModalProps,
} from './LuiModalAsyncContext';
import { LuiModalAsyncInstanceContext } from './LuiModalAsyncInstanceContext';
import { useInterval } from '../common/useInterval';
import { v4 } from '../common/uuid';

export interface LuiModalAsyncInstance {
  uuid: string;
  ownerRef: MutableRefObject<HTMLElement | null>;
  componentInstance: ReactElement;
  // eslint-disable-next-line
  resolve: (result: any) => void;
  showModalProps?: ShowModalProps;
}

/**
 * Provides the ability to show modals using react components without needing useState boilerplate and inline dialogs.
 *
 * To use:
 * <ol>
 * <li>Add this Provider somewhere in your standard providers
 * <pre>
 * <LuiModalAsyncContextProvider>
 *    ...children
 * </LuiModalAsyncContextProvider>
 * </pre>
 * </li>
 * <li>Add the modal return type to the element as below, and use the props resolve/close from ModalProps for results.
 * <pre>
 * export interface SomeModalProps extends ModalProps<number> {
 *   someProp?: number; // user props
 * }
 *
 * export const SomeModal = ({initialState, resolve, close}: SomeModalProps): ReactElement => {
 *    return Modal(
 *       <div>
 *           Itsa me, I'm a modal
 *          <button onClick={close}>Cancel</button>
 *          <button onClick={()=>resolve(someProp)}>Save</button>
 *       </div>
 *    )}
 * }
 * </pre>
 * </li>
 * <li> To show the dialog and get the result...
 * <pre>
 *   // Note: modalOwnerRef is only required if you need to support popout windows
 *   const { showModal, modalOwnerRef  } = useShowAsyncModal();
 *   ...
 *   const showModal = () => {
 *     const result = await showModal(SomeModal, { someProp: 1 });
 *     if (!result) return; // modal cancelled
 *   }
 *
 *   return <button onClick={showModal} ref={modalOwnerRef}>Show Modal!</button>
 * </pre>
 * </li>
 * </ol>
 */
export const LuiModalAsyncContextProvider = ({ children }: PropsWithChildren<unknown>): ReactElement => {
  const [modals, setModals] = useState<LuiModalAsyncInstance[]>([]);
  const openWindowsRef = useRef([window]);

  // hook window open for opening dialogs across multiple windows
  useEffect(() => {
    const oldWindowOpen = window.open;
    window.open = (...props) => {
      const r = oldWindowOpen(...props);
      // We don't want to track non app-windows, e.g. help screens
      if (!props[0]) {
        if (r) {
          openWindowsRef.current.push(r as typeof window);
        }
      }
      return r;
    };

    return () => {
      window.open = oldWindowOpen;
    };
  }, []);

  /**
   * Inserts the modal into the page, and removes once modal has a result.
   * Note: full generic types are provided by the interface.  Function def has been simplified here.
   *
   * @param ownerRef Reference to element that opened this dialog such that it works for popout windows.
   * @param Component React component.
   * @param args Arguments for react component.
   */
  const showModal = useCallback(
    (
      ownerRef: MutableRefObject<HTMLElement | null>,
      Component: ComponentType,
      // eslint-disable-next-line
      args: any,
      showModalProps?: ShowModalProps,
    ): // eslint-disable-next-line
    PromiseWithResolve<any> => {
      if (!('level' in args)) {
        debugLog(`Show generic modal`, args);
      }
      const uuid = v4();
      // eslint-disable-next-line
      let extResolve: PromiseWithResolve<any>['resolve'];

      // @ts-expect-error promise resolve cast
      // eslint-disable-next-line
      const promise: PromiseWithResolve<any> = new Promise((resolve) => {
        extResolve = resolve;
        try {
          // If there are any exceptions the modal won't show
          setModals((modals) => [
            ...modals,
            {
              uuid,
              ownerRef,
              // eslint-disable-next-line
              componentInstance: <Component {...args} resolve={resolve} close={() => resolve(undefined)} />,
              resolve,
              showModalProps,
            },
          ]);
        } catch (e) {
          console.error(e);
          return;
        }
      }).then((result) => {
        debugLog('Modal resolved result =', JSON.stringify(result));
        return new Promise((resolve) => {
          // Close modal
          setModals((modals) => {
            // Defer resolving to give react a chance to update the view allowing the async event model to run.
            // Just in case the task following this is long-running with non IO code, which will block the event queue.
            setTimeout(() => resolve(result), 10);
            return modals.filter((e) => e.uuid !== uuid);
          });
        });
      });
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      promise.resolve = extResolve!;
      return promise;
    },
    [],
  );

  /**
   * Check modal is still attached to a window.
   */
  const modalHasView = useCallback(
    (modalInstance: LuiModalAsyncInstance): boolean =>
      modalInstance.ownerRef === openModalOnAllWindows || !!modalInstance.ownerRef.current?.ownerDocument.defaultView,
    [],
  );

  const windowIsOpen = useCallback((w: typeof window): boolean => !w.closed, []);

  /**
   * Tidy up modals that have closed because of an external window closing
   */
  useInterval(() => {
    const newModals = modals.filter(modalHasView);
    if (newModals.length !== modals.length) {
      setModals(newModals);
    }
    // We could do this with window close listeners, but they are unreliable
    openWindowsRef.current = openWindowsRef.current.filter((w) => !w.closed);

    newModals.forEach((modalInstance) => {
      if (modalInstance.showModalProps?.polledCloseCondition?.()) {
        modalInstance.resolve(undefined);
      }
    });
  }, 500);

  const portalOwners = (modalInstance: LuiModalAsyncInstance) =>
    modalInstance.ownerRef == openModalOnAllWindows
      ? openWindowsRef.current.filter(windowIsOpen).map((frame) => frame.document.body)
      : [(modalInstance.ownerRef.current?.ownerDocument ?? document).body];

  return (
    <LuiModalAsyncContext.Provider
      value={{
        showModal,
      }}
    >
      <>
        {
          // oxlint-disable-next-line react/refs
          modals.filter(modalHasView).map((modalInstance) => (
            <LuiModalAsyncInstanceContext.Provider
              key={modalInstance.uuid}
              value={{
                ownerRef: modalInstance.ownerRef,
                close: () => modalInstance.resolve(undefined),
                resolve: modalInstance.resolve,
              }}
            >
              {portalOwners(modalInstance).map((element) =>
                createPortal(<>{modalInstance.componentInstance}</>, element),
              )}
            </LuiModalAsyncInstanceContext.Provider>
          ))
        }
      </>
      <>{children}</>
    </LuiModalAsyncContext.Provider>
  );
};
